import { supabase } from '../lib/supabase';

export const reconciliationService = {
  /**
   * Get reconciliation data with filters
   */
  async getReconciliationData(tenantId, filters = {}) {
    try {
      let query = supabase
        ?.from('transaction_reconciliation')
        ?.select('*')
        ?.eq('tenant_id', tenantId)
        ?.order('transaction_date', { ascending: false });

      if (filters?.status) query = query?.eq('reconciliation_status', filters?.status);
      if (filters?.transactionType) query = query?.eq('transaction_type', filters?.transactionType);
      if (filters?.startDate) query = query?.gte('transaction_date', filters?.startDate);
      if (filters?.endDate) query = query?.lte('transaction_date', filters?.endDate);

      const { data, error } = await query;
      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching reconciliation data:', error);
      throw error;
    }
  },

  /**
   * Update reconciliation status
   */
  async updateReconciliationStatus(reconciliationId, status, userId, notes) {
    try {
      const { data, error } = await supabase
        ?.from('transaction_reconciliation')
        ?.update({
          reconciliation_status: status,
          reconciled_by: userId,
          reconciled_at: new Date()?.toISOString(),
          resolution_notes: notes,
          updated_at: new Date()?.toISOString()
        })
        ?.eq('id', reconciliationId)
        ?.select()
        ?.single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error updating reconciliation status:', error);
      throw error;
    }
  },

  /**
   * Match transactions
   */
  async matchTransactions(reconciliationId1, reconciliationId2, userId) {
    try {
      // Update both transactions as matched
      const { error: error1 } = await supabase
        ?.from('transaction_reconciliation')
        ?.update({
          reconciliation_status: 'matched',
          matched_transaction_id: reconciliationId2,
          reconciled_by: userId,
          reconciled_at: new Date()?.toISOString()
        })
        ?.eq('id', reconciliationId1);

      if (error1) throw error1;

      const { error: error2 } = await supabase
        ?.from('transaction_reconciliation')
        ?.update({
          reconciliation_status: 'matched',
          matched_transaction_id: reconciliationId1,
          reconciled_by: userId,
          reconciled_at: new Date()?.toISOString()
        })
        ?.eq('id', reconciliationId2);

      if (error2) throw error2;

      return { success: true };
    } catch (error) {
      console.error('Error matching transactions:', error);
      throw error;
    }
  },

  /**
   * Get reconciliation exceptions
   */
  async getReconciliationExceptions(tenantId, filters = {}) {
    try {
      let query = supabase
        ?.from('reconciliation_exceptions')
        ?.select('*')
        ?.eq('tenant_id', tenantId)
        ?.order('created_at', { ascending: false });

      if (filters?.status) query = query?.eq('status', filters?.status);
      if (filters?.severity) query = query?.eq('severity', filters?.severity);
      if (filters?.exceptionType) query = query?.eq('exception_type', filters?.exceptionType);

      const { data, error } = await query;
      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching reconciliation exceptions:', error);
      throw error;
    }
  },

  /**
   * Resolve exception
   */
  async resolveException(exceptionId, userId, resolutionNotes) {
    try {
      const { data, error } = await supabase
        ?.from('reconciliation_exceptions')
        ?.update({
          status: 'resolved',
          resolved_by: userId,
          resolved_at: new Date()?.toISOString(),
          resolution_notes: resolutionNotes
        })
        ?.eq('id', exceptionId)
        ?.select()
        ?.single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error resolving exception:', error);
      throw error;
    }
  },

  /**
   * Get audit log
   */
  async getAuditLog(tenantId, reconciliationId = null) {
    try {
      let query = supabase
        ?.from('reconciliation_audit_log')
        ?.select('*, user_profiles:performed_by(full_name, email)')
        ?.eq('tenant_id', tenantId)
        ?.order('created_at', { ascending: false });

      if (reconciliationId) query = query?.eq('reconciliation_id', reconciliationId);

      const { data, error } = await query;
      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching audit log:', error);
      throw error;
    }
  },

  /**
   * Import bank transactions
   */
  async importBankTransactions(tenantId, transactions, userId) {
    try {
      const records = transactions?.map(t => ({
        tenant_id: tenantId,
        bank_name: t?.bankName,
        account_number_last4: t?.accountLast4,
        transaction_date: t?.date,
        description: t?.description,
        amount: t?.amount,
        transaction_reference: t?.reference,
        imported_by: userId
      }));

      const { data, error } = await supabase
        ?.from('bank_transaction_imports')
        ?.insert(records)
        ?.select();

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error importing bank transactions:', error);
      throw error;
    }
  },

  /**
   * Get bank transaction imports
   */
  async getBankTransactionImports(tenantId, filters = {}) {
    try {
      let query = supabase
        ?.from('bank_transaction_imports')
        ?.select('*')
        ?.eq('tenant_id', tenantId)
        ?.order('transaction_date', { ascending: false });

      if (filters?.isReconciled !== undefined) query = query?.eq('is_reconciled', filters?.isReconciled);
      if (filters?.startDate) query = query?.gte('transaction_date', filters?.startDate);
      if (filters?.endDate) query = query?.lte('transaction_date', filters?.endDate);

      const { data, error } = await query;
      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching bank transaction imports:', error);
      throw error;
    }
  }
};

export default reconciliationService;